import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  MenuItem,
  Box,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type {
  Athlete,
  CreateAthleteDto,
  AthleteAgeCategory,
  Gender,
} from '../../types/domain';
import { ATHLETE_AGE_CATEGORY_LABELS } from '../../types/domain';

interface Props {
  open: boolean;
  athlete: Athlete | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateAthleteDto) => void;
}

const ALL_CATEGORIES: AthleteAgeCategory[] = [
  'MLADSI_ZAK',
  'STARSI_ZAK',
  'MLADSI_DOROSTENEC',
  'STREDNI_DOROSTENEC',
  'STARSI_DOROSTENEC',
  'MLADSI_DOROSTENKA',
  'STREDNI_DOROSTENKA',
  'STARSI_DOROSTENKA',
  'MUZ',
  'ZENA',
  'OSTATNI',
];

export function AthleteDialog({ open, athlete, loading, onCancel, onSubmit }: Props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthYear, setBirthYear] = useState<string>('');
  const [gender, setGender] = useState<Gender>('M');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [ageCategory, setAgeCategory] = useState<AthleteAgeCategory>('OSTATNI');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      if (athlete) {
        setFirstName(athlete.firstName);
        setLastName(athlete.lastName);
        setBirthYear(athlete.birthYear?.toString() ?? '');
        setGender(athlete.gender);
        setRegistrationNumber(athlete.registrationNumber ?? '');
        setAgeCategory(athlete.ageCategory);
        setNote(athlete.note ?? '');
      } else {
        setFirstName('');
        setLastName('');
        setBirthYear('');
        setGender('M');
        setRegistrationNumber('');
        setAgeCategory('OSTATNI');
        setNote('');
      }
    }
  }, [open, athlete]);

  const submit = () => {
    onSubmit({
      firstName,
      lastName,
      birthYear: birthYear ? Number(birthYear) : undefined,
      gender,
      registrationNumber: registrationNumber || undefined,
      ageCategory,
      note: note || undefined,
    });
  };

  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{athlete ? 'Upravit závodníka' : 'Nový závodník'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Jméno"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Příjmení"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Ročník"
              type="number"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              fullWidth
            />
            <TextField
              label="Pohlaví"
              select
              value={gender}
              onChange={(e) => setGender(e.target.value as Gender)}
              fullWidth
            >
              <MenuItem value="M">Muž</MenuItem>
              <MenuItem value="F">Žena</MenuItem>
            </TextField>
          </Box>
          <TextField
            label="Kategorie"
            select
            value={ageCategory}
            onChange={(e) => setAgeCategory(e.target.value as AthleteAgeCategory)}
            fullWidth
          >
            {ALL_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {ATHLETE_AGE_CATEGORY_LABELS[c]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Evidenční číslo"
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
            fullWidth
          />
          <TextField
            label="Poznámka"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Zrušit
        </Button>
        <Button onClick={submit} variant="contained" disabled={!canSubmit || loading}>
          {athlete ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
